import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, FileText } from 'lucide-react';
import { VA_BLACK, NAVY } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL } from '../../../../shared/constants';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: '600',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ReceiptViewerModal({ receipt, onClose, onPrev, onNext, hasPrev, hasNext }) {
  // Keyboard navigation
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape')       onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  if (!receipt) return null;

  const isImage = receipt.fileType?.startsWith('image/');
  const isPdf   = receipt.fileType === 'application/pdf';

  const navArrowStyle = {
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: '#fff',
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.15s',
  };

  const disabledArrowStyle = {
    ...navArrowStyle,
    opacity: 0.2,
    cursor: 'default',
    pointerEvents: 'none',
  };

  return (
    /* Overlay — click to close */
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      {/* Header — stop propagation so clicks on header don't close modal */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: VA_BLACK,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          padding: '0 16px',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
        }}
      >
        {/* Left: receipt metadata */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: '700', fontSize: 14, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
            {receipt.vendor || '—'}
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
            {receipt.employeeName}
          </span>
          <StatusBadge status={receipt.displayStatus || receipt.status} />
          <span style={{ fontWeight: '700', fontSize: 13, color: '#d97706', whiteSpace: 'nowrap' }}>
            {fmt(receipt.amount)}
          </span>
          {receipt.date && (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
              {receipt.date}
            </span>
          )}
        </div>

        {/* Center: file name */}
        <div style={{
          fontSize: 11, color: 'rgba(255,255,255,0.35)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 200, flexShrink: 1,
        }}>
          {receipt.filename || ''}
        </div>

        {/* Right: close button */}
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.7)',
            borderRadius: 6,
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Viewer area */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: '16px',
          minHeight: 0,
        }}
      >
        {/* Prev arrow */}
        <button
          onClick={onPrev}
          style={hasPrev ? navArrowStyle : disabledArrowStyle}
          title="Recibo anterior (←)"
        >
          <ChevronLeft size={22} />
        </button>

        {/* Content */}
        <div style={{
          flex: 1,
          maxWidth: '85vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
        }}>
          {isImage && receipt.previewUrl ? (
            <AuthedReceiptPreview
              src={receipt.previewUrl}
              fileType={receipt.fileType}
              alt={receipt.vendor || 'receipt'}
              imgStyle={{
                maxHeight: '82vh',
                maxWidth: '85vw',
                objectFit: 'contain',
                borderRadius: 8,
                boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          ) : isPdf && receipt.previewUrl ? (
            <AuthedReceiptPreview
              src={receipt.previewUrl}
              fileType={receipt.fileType}
              alt={receipt.vendor || 'receipt'}
              iframeStyle={{
                width: '85vw',
                height: '82vh',
                border: 'none',
                borderRadius: 8,
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          ) : (
            /* Fallback: no preview URL or unknown type */
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              color: 'rgba(255,255,255,0.4)',
            }}>
              <FileText size={64} />
              <div style={{ fontSize: 14 }}>Vista previa no disponible</div>
            </div>
          )}
        </div>

        {/* Next arrow */}
        <button
          onClick={onNext}
          style={hasNext ? navArrowStyle : disabledArrowStyle}
          title="Siguiente recibo (→)"
        >
          <ChevronRight size={22} />
        </button>
      </div>
    </div>
  );
}
